/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { APPLICANT_ATTRIBUTION_KEYS, CUSTOMER_DATA_TABS, LEAD_DETAILS_FORMS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AccountHolder from './AccountHolder.tsx';
import ContactInfo from './ContactInfo.tsx';
import { HashDialog } from './HashDialog.tsx';
import PersonalDetails from './PersonalDetails.tsx';
import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import { ApplicantCollapsible } from '~/features/leadDetails/components/Overview/CustomerData/shared/ApplicantCollapsible.tsx';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import useApplicantNotes from '~/features/leadDetails/hooks/useApplicantNotes.ts';
import { extractErrorPaths, useTabErrorNotification } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import type { SchemaPersonal } from '~/features/leadDetails/schemas/personal.schema.ts';
import { schemaPersonal } from '~/features/leadDetails/schemas/personal.schema.ts';
import generatePersonalDataCommands from '~/features/leadDetails/utils/generatePersonalDataCommands.ts';
import { submitCommands } from '~/features/leadDetails/utils/submitCommands.ts';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange.tsx';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const formId = LEAD_DETAILS_FORMS.PERSONAL_DATA;

const PersonalData: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const { registerDirtyState, registerReset, registerSubmit } = useLeadDetailsFormContext();
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogValidation, setDialogValidation] = useState({ hashChange: false, noteDuplicate: false });
    const [hashChangeCheck, setHashChangeCheck] = useState<boolean>(false);
    const [resolveDialogPromise, setResolveDialogPromise] = useState<null | ((_arg: typeof dialogValidation) => void)>(
        null,
    );
    const [resolveHashChangePromise, setResolveHashChangePromise] = useState<null | ((_arg: string[]) => void)>(null);
    const [hasOwnApplicantNotes, setHasOwnApplicantNotes] = useState({
        applicant1: false,
        applicant2: false,
    });
    const { tab } = useCustomerDataTabsContext();
    const applicantIds = [lead?.antragsteller1.id, lead?.antragsteller2?.id].filter(Boolean) as string[];
    const { data: applicantNotes } = useApplicantNotes(applicantIds, lead.id);

    const methods = useForm<SchemaPersonal>({
        defaultValues: lead as unknown as SchemaPersonal,
        mode: 'onChange',
        resetOptions: {
            keepValues: true,
        },
        resolver: zodResolver(schemaPersonal),
        shouldUnregister: true,
    });

    useTabErrorNotification(CUSTOMER_DATA_TABS.PERSONAL_DATA, methods.formState.errors);

    // NOTE: we need to set the default values for the antragsteller2 field when the second applicant is added to the form
    const hadAntragsteller2 = useRef(!!lead.antragsteller2);
    useEffect(() => {
        if (!hadAntragsteller2.current && lead.antragsteller2) {
            hadAntragsteller2.current = true;
            methods.resetField('antragsteller2', {
                defaultValue: lead.antragsteller2,
                keepDirty: false,
                keepError: false,
                keepTouched: false,
            });
        }
    }, [lead, lead.antragsteller2, methods]);

    useEffect(() => {
        registerDirtyState(formId, {
            dirty: methods.formState.isDirty,
            dirtyFields: methods.formState.dirtyFields,
        });
    }, [registerDirtyState, methods.formState, methods.formState.isDirty]);

    registerReset(formId, methods.reset);

    useEffect(() => {
        setHasOwnApplicantNotes({
            applicant1: !!applicantNotes?.applicant1Notes.notes.find(
                (item) => item.applicantId === lead?.antragsteller1.id,
            ),
            applicant2: !!applicantNotes?.applicant2Notes.notes.find(
                (item) => item.applicantId === lead?.antragsteller2?.id,
            ),
        });
    }, [applicantNotes, lead?.antragsteller1.id, lead?.antragsteller2?.id]);

    // if we have an email change in the legal/consent modal, we need to update the email in the form
    useEffect(() => {
        methods.setValue('antragsteller1.personendaten.email', lead.antragsteller1.personendaten.email ?? '');
    }, [lead.antragsteller1.personendaten.email, methods]);

    useEffect(() => {
        if (lead.antragsteller2?.personendaten?.email)
            methods.setValue('antragsteller2.personendaten.email', lead.antragsteller2.personendaten.email);
    }, [lead.antragsteller2?.personendaten.email, methods]);

    useEffect(() => {
        if (hashChangeCheck) {
            const involvedApplicantIds: string[] = [];
            for (const key of Object.values(APPLICANT_ATTRIBUTION_KEYS)) {
                if (
                    typeof methods.formState.dirtyFields[key] !== 'boolean' &&
                    (methods.formState.dirtyFields[key]?.personendaten?.geburtsdatum ||
                        methods.formState.dirtyFields[key]?.personendaten?.vorname ||
                        methods.formState.dirtyFields[key]?.personendaten?.nachname)
                ) {
                    const id = lead[key]?.id;
                    const hasOwnNotes = !!hasOwnApplicantNotes[key === 'antragsteller1' ? 'applicant1' : 'applicant2'];
                    if (id && hasOwnNotes) involvedApplicantIds.push(id);
                }
            }
            if (resolveHashChangePromise) {
                resolveHashChangePromise(involvedApplicantIds);
                setResolveHashChangePromise(null);
                setHashChangeCheck(false);
            }
        }
    }, [hasOwnApplicantNotes, hashChangeCheck, lead, methods.formState.dirtyFields, resolveHashChangePromise]);

    useEffect(() => {
        if (!openDialog && resolveDialogPromise) {
            // Resolve the promise and give the validation object to the function
            resolveDialogPromise(dialogValidation);
            setResolveDialogPromise(null);
        }
    }, [dialogValidation, openDialog, resolveDialogPromise]);

    // Why ? Promise is needed to have the states within the handleSubmit (involvedApplicantIds)
    // When ? Promise is triggered when the save button is clicked
    // How ? Resolve is given to state and will be resolved when useEffect is done
    async function waitForHashChangeCheck() {
        return new Promise<string[]>((resolve) => {
            setResolveHashChangePromise(() => resolve);
            setHashChangeCheck(true);
        });
    }

    // Why ? Promise is needed to have the states within the handleSubmit
    // When ? Promise is triggered when hash change is detected and the applicant has applicant notes
    // How ? Resolve is given to state and will be resolved when dialog is closed in useEffect [openDialog]
    const waitForDialogToClose = useCallback(() => {
        return new Promise<typeof dialogValidation>((resolve) => {
            setResolveDialogPromise(() => resolve);
        });
    }, []);

    useOnMountAndChange(() => {
        registerSubmit(
            (setSubmitStatus) =>
                methods.handleSubmit(
                    async (data: SchemaPersonal) => {
                        const commandArgs = {
                            applicantId1: lead.antragsteller1.id,
                            applicantId2: lead.antragsteller2?.id,
                            newLead: data,
                            oldLead: lead as unknown as SchemaPersonal,
                        };
                        waitForHashChangeCheck().then(async (ids) => {
                            if (ids.length > 0) {
                                setOpenDialog(true);
                                await waitForDialogToClose().then(async (validation) => {
                                    if (validation.hashChange) {
                                        const commands = generatePersonalDataCommands({
                                            ...commandArgs,
                                            ...(!validation.noteDuplicate && {
                                                ApplicantIdsForDeletingNotes: ids,
                                            }),
                                        });
                                        await submitCommands(lead.id, setSubmitStatus, commands, methods.reset);
                                    }
                                    if (!validation.hashChange && !validation.noteDuplicate) {
                                        setSubmitStatus({ breakSubmit: ['living'], changes: true, saved: false });
                                    }
                                });
                            } else {
                                const commands = generatePersonalDataCommands(commandArgs);
                                await submitCommands(lead.id, setSubmitStatus, commands, methods.reset);
                            }
                        });
                        setDialogValidation({ hashChange: false, noteDuplicate: false });
                    },
                    (error) => {
                        setSubmitStatus({ changes: true, error, saved: false });
                    },
                ),
            formId,
            methods.trigger,
        );
    }, [lead, lead.id, methods, registerSubmit, t]);

    return (
        <FormProvider {...methods}>
            <HashDialog
                dialogValidation={dialogValidation}
                hasOwnApplicantNotes={hasOwnApplicantNotes}
                openDialog={openDialog}
                setDialogValidation={setDialogValidation}
                setOpenDialog={setOpenDialog}
            />
            <form id={formId} style={hide(tab !== CUSTOMER_DATA_TABS.PERSONAL_DATA)}>
                <div className="mb-2 space-y-2">
                    <ApplicantCollapsible
                        applicant={1}
                        collapsible={lead.antragsteller2 != null}
                        errorCount={extractErrorPaths(methods.formState.errors.antragsteller1 ?? {}).length}
                        testId="personal-data-applicant1"
                    >
                        <PersonalDetails applicant={1} applicantId={applicantIds[0]} />
                        <ContactInfo applicant={1} />
                    </ApplicantCollapsible>
                    {lead.antragsteller2 && (
                        <ApplicantCollapsible
                            applicant={2}
                            collapsible
                            errorCount={extractErrorPaths(methods.formState.errors.antragsteller2 ?? {}).length}
                            style={hide(!!lead.coApplicantHidden)}
                            testId="personal-data-applicant2"
                        >
                            <PersonalDetails applicant={2} applicantId={applicantIds[1]} />
                            <ContactInfo applicant={2} secondApplicantHidden={lead.coApplicantHidden} />
                        </ApplicantCollapsible>
                    )}
                    <AccountHolder twoApplicants={lead.antragsteller2 != null} />
                </div>
                {lead.antragsteller2 && (
                    <div className="flex justify-center my-6" style={hide(!lead.coApplicantHidden)}>
                        <span className="text-gray-400">{t('OVERVIEW.SECOND_APPLICANT_HIDDEN')}</span>
                    </div>
                )}
            </form>
        </FormProvider>
    );
};

export default PersonalData;
